import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import { Text, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import SVG from 'helpers/SVG/SVG';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import classNames from 'classnames';
import WidgetWrapper from 'helpers/WidgetWrapper/WidgetWrapper';
import {
  FilterAnyOf,
  FilterAnd,
  EntityModel,
  useSearchResults,
  WidgetDataType,
  widget,
  FilterNot,
} from '@sitecore-search/react';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { formatTimestamp } from 'lib/utils/date-utils';
import {
  IconProps,
  ElementProps,
  ResourceItemProps,
  ResourceLibraryProps,
  ResourceLibraryStyle,
  variantsTypes,
  getResourceType,
  mapFieldValues,
  createBaseFilters,
  createTypeTopicFilters,
  transformIncludedResources,
  updateQueryString,
  filterIncludedResources,
  buildUpdatedContentType,
  buildUpdatedTopics,
  getResourceTypeDisplayName,
  getResourceFilterType,
} from './ResourceLibrary.helpers';

const ResourceLibrary = (props: ResourceLibraryProps) => {
  const { currentScreenWidth, isScreenTypeDetected } = useCurrentScreenType();
  if (!isScreenTypeDetected) return <></>;

  return (
    <WidgetWrapper>
      <ResourceWidget
        props={props}
        rfkId="related_resources"
        InitialCount={
          (currentScreenWidth <= getBreakpoint('desktop')
            ? props?.fields?.InitialResourceMobileCount?.value
            : props?.fields?.InitialResourceDesktopCount?.value) as number
        }
        currentScreenWidth={currentScreenWidth}
      />
    </WidgetWrapper>
  );
};

export default ResourceLibrary;

const ResourceLibrarySearch = ({
  props,
  InitialCount,
  currentScreenWidth,
}: {
  props: ResourceLibraryProps;
  InitialCount: number;
  currentScreenWidth: number;
}) => {
  const { fields = undefined, params = undefined } = props;
  const dropdownRefs = React.useRef<HTMLDivElement | null>(null);

  //Alignment Styling
  const { HeadingStyle, checkBokStyle } = ResourceLibraryStyle();
  const alignment = params?.Alignment?.Value?.value === 'text-center' ? 'center' : 'left';
  const headingClass = HeadingStyle({
    positions: alignment,
    tagStyle: (params?.HeadingType?.Styles?.value || 'default') as variantsTypes['tagStyle'],
  });

  //Partial Updates
  const [contentType, setContentType] = React.useState<string[]>([]);
  const [topics, setTopics] = React.useState<string[]>([]);
  const [finalResult, setFinalResult] = React.useState<number>(0);
  const [resourcesData, setResourcesData] = React.useState<{
    ResourceList: Array<EntityModel>;
    fetchCount: number;
  }>({ ResourceList: [], fetchCount: 1 });

  //Actual Updates
  const [finalContentType, setFinalContentType] = React.useState<string[]>([]);
  const [finalTopics, setFinalTopics] = React.useState<string[]>([]);
  const [oldResult, setOldResult] = React.useState<number>(0);
  const [actualData, setActualData] = React.useState<{
    ResourceList: Array<EntityModel>;
    fetchCount: number;
  }>({ ResourceList: [], fetchCount: 1 });

  const [directChange, setDirectChange] = React.useState<boolean>(false);
  const [isListView, setIsListView] = React.useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = React.useState<boolean>(false);
  const [firstTimeCount, setFirstTimeCount] = React.useState<number>(0);

  //State for IncludeResource
  const [includedResource, setIncludedResource] = React.useState<ResourceItemProps[]>();

  //Search Query Box
  const searchSource = process.env.RESOURCES_SEARCH_SOURCE_ID;
  const searchSourceIds = searchSource?.split('|') || [];

  //Scrollable Content
  const scrollableContentRef = React.useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  // Close dropdown when clicking outside
  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownOpen !== null) {
      const clickedInsideDropdown = dropdownRefs.current?.contains(event.target as Node);
      if (!clickedInsideDropdown) {
        if (dropdownOpen) {
          setTopics([...finalTopics]);
          setContentType([...finalContentType]);
          setFinalResult(oldResult);

          // Update query strings to match the reverted state
          updateTypesQueryString(finalContentType);
          updateTopicsQueryString(finalTopics);

          const updatedFilters: (FilterAnyOf | FilterNot)[] = [...baseFilter()];

          if (contentType.length > 0) {
            updatedFilters.push(new FilterAnyOf('type', contentType));
          } else {
            updatedFilters.push(new FilterAnyOf('type', typesItems));
          }

          if (topics.length > 0) {
            updatedFilters.push(new FilterAnyOf('topics', topics));
          } else {
            updatedFilters.push(new FilterAnyOf('topics', topicsItems));
          }

          query
            .getRequest()
            .setSearchFacetAll(false)
            .setSearchOffset(0)
            .setSources(searchSourceIds)
            .setSearchFilter(
              updatedFilters.length > 0 ? new FilterAnd(updatedFilters) : new FilterAnd(filters)
            );
        }
        setDropdownOpen(false);
      }
    }
  };

  // Query Array Object
  const filters: (FilterAnyOf | FilterNot)[] = [];

  const baseFilter = () => createBaseFilters(props, mapFieldValues);

  let typesItems: string[] = [];
  let topicsItems: string[] = [];

  const baseTypeTopicFilter = () => {
    const result = createTypeTopicFilters(fields, getResourceType);
    typesItems = result.typesItems;
    topicsItems = result.topicsItems;
    return result.filters;
  };

  filters.push(...baseFilter());
  filters.push(...baseTypeTopicFilter());

  const IncludedResources = () =>
    transformIncludedResources(props?.fields?.ArticlesToInclude || [], getResourceType);

  const loadMoreOptions = {
    loadMoreText: fields?.ViewMoreContent?.value as string,
    loadCount: `${fields?.NumberToDisplay?.value}`,
  };

  // Function to handle the Load more more Result
  const loadMoreResults = () => {
    const _loadCount = Number(loadMoreOptions?.loadCount);
    if (actualData.fetchCount >= 1) {
      query.getRequest().setSearchLimit(_loadCount).setSearchOffset(actualData.ResourceList.length);
    }
  };

  const updateTypesQueryString = (typesArray: string[]) => updateQueryString('types', typesArray);

  const activeTypes = ({
    type,
    isDropdownValue = false,
  }: {
    type: string;
    isDropdownValue?: boolean;
  }) => {
    const newType = getResourceType(type);
    const passObject: {
      remove: boolean;
      field: string;
      value: string;
    } = {
      remove: false,
      field: 'contentType',
      value: newType,
    };

    if (contentType) {
      if (contentType.some((item) => item === newType)) {
        const updatedTypes = contentType.filter((item) => item !== newType);
        setContentType(updatedTypes);
        passObject.remove = true;

        if (isDropdownValue) {
          setFinalContentType(updatedTypes);
        }

        // Update query string with updated types
        updateTypesQueryString(updatedTypes);
      } else {
        const newTypes = [...contentType, newType];
        setContentType(newTypes);
        if (isDropdownValue) {
          setFinalContentType(newTypes);
        }

        // Update query string with new types
        updateTypesQueryString(newTypes);
      }
    } else {
      setContentType([newType]);
      if (isDropdownValue) {
        setFinalContentType([newType]);
      }

      // Update query string with type
      updateTypesQueryString([newType]);
    }

    if (isDropdownValue) {
      setDirectChange(true);
    }

    apiCall(passObject);
  };

  const updateTopicsQueryString = (topicsArray: string[]) =>
    updateQueryString('topics', topicsArray);

  const activeTopics = ({
    topic,
    isDropdownValue = false,
  }: {
    topic: string;
    isDropdownValue?: boolean;
  }) => {
    const passObject: {
      remove: boolean;
      field: string;
      value: string;
    } = {
      remove: false,
      field: 'topic',
      value: topic,
    };

    if (topics) {
      if (topics.some((item) => item === topic)) {
        const updatedTopic = topics.filter((item) => item !== topic);
        setTopics(updatedTopic);
        passObject.remove = true;

        if (isDropdownValue) {
          setFinalTopics(updatedTopic);
        }

        // Update query string with updated topics
        updateTopicsQueryString(updatedTopic);
      } else {
        const newTopics = [...topics, topic];
        setTopics(newTopics);
        if (isDropdownValue) {
          setFinalTopics(newTopics);
        }

        // Update query string with new topics
        updateTopicsQueryString(newTopics);
      }
    } else {
      setTopics([topics]);
      if (isDropdownValue) {
        setFinalTopics([topics]);
      }

      // Update query string with topics
      updateTopicsQueryString([topics]);
    }

    if (isDropdownValue) {
      setDirectChange(true);
    }

    apiCall(passObject);
  };

  const filterIncludeResource = (types: string[], topics: string[]) => {
    const filteredResources = filterIncludedResources(IncludedResources(), types, topics);

    if (types.length > 0 || topics.length > 0) {
      setIncludedResource(filteredResources);
    }
  };

  const apiCall = (activeObj: { field: string; value: string; remove: boolean }) => {
    const updatedFilters: (FilterAnyOf | FilterNot)[] = [...baseFilter()];
    let updatedContentType: string[] = [];
    let updatedTopics: string[] = [];

    if (activeObj.field == 'contentType' || contentType.length > 0) {
      updatedContentType = buildUpdatedContentType(activeObj, contentType);

      if (updatedContentType.length > 0) {
        updatedFilters.push(new FilterAnyOf('type', updatedContentType));
      }
    }

    if (activeObj.field == 'topic' || topics.length > 0) {
      updatedTopics = buildUpdatedTopics(activeObj, topics);

      if (updatedTopics.length > 0) {
        updatedFilters.push(new FilterAnyOf('topics', updatedTopics));
      }
    }

    if (updatedContentType.length === 0 && typesItems.length > 0) {
      updatedFilters.push(new FilterAnyOf('type', typesItems));
    }

    if (updatedTopics.length === 0 && topicsItems.length > 0) {
      updatedFilters.push(new FilterAnyOf('topics', topicsItems));
    }

    query
      .getRequest()
      .setSearchFacetAll(false)
      .setSearchOffset(0)
      .setSources(searchSourceIds)
      .setSearchFilter(
        updatedFilters.length > 0 ? new FilterAnd(updatedFilters) : new FilterAnd(filters)
      );
  };

  const viewResult = () => {
    setDropdownOpen(false);
    setOldResult(total_item);

    setFinalContentType([...contentType]);
    setFinalTopics([...topics]);

    filterIncludeResource(contentType, topics);

    if (total_item === finalResult) {
      setActualData({
        fetchCount: 1,
        ResourceList: [...resourcesData.ResourceList],
      });
    }
  };

  const clearFilter = () => {
    setContentType([]);
    setTopics([]);
    setFinalContentType([]);
    setFinalTopics([]);
    setFirstTimeCount(0);

    filterIncludeResource(typesItems, topicsItems);

    // Clear query string parameters
    const params = new URLSearchParams(window.location.search);
    params.delete('types');
    params.delete('topics');
    const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    window.history.pushState({}, '', newUrl);

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    filters && filters.length > 0
      ? query
          .getRequest()
          .setSearchFacetAll(false)
          .setSources(searchSourceIds)
          .setSearchFilter(new FilterAnd(filters))
      : null;

    setDropdownOpen(false);
  };

  const closeDropDown = () => {
    setTopics([...finalTopics]);
    setContentType([...finalContentType]);
    setFinalResult(oldResult);
    setDropdownOpen(false);
  };
  const pathName = usePathname();
  //Window Load Query
  const {
    query,
    queryResult: {
      isInitialLoading,
      isFetched,
      isFetching,
      data: { total_item = 0, limit = 0, offset = 0, content: resources = [] } = {},
    },
  } = useSearchResults({
    query: (query) => {
      if (filters && filters.length > 0) {
        query
          .getRequest()
          .setSearchFacetAll(false)
          .setSources(searchSourceIds)
          .setSearchFilter(new FilterAnd(filters));
      }
    },
    state: {
      itemsPerPage: Number(InitialCount),
      page: 1,
    },
  });

  // Load filters from query string on component mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const typesParam = params.get('types');
    const topicsParam = params.get('topics');

    if (typesParam || topicsParam) {
      const typesFromUrl = typesParam ? typesParam.split(',').filter((t) => t.trim()) : [];
      const topicsFromUrl = topicsParam ? topicsParam.split(',').filter((t) => t.trim()) : [];

      // Validate types against valid typesItems
      const validTypesFromUrl = typesFromUrl.filter((type) => typesItems.includes(type));

      // Validate topics against valid topicsItems
      const validTopicsFromUrl = topicsFromUrl.filter((topic) => topicsItems.includes(topic));

      if (validTypesFromUrl.length > 0) {
        setContentType(validTypesFromUrl);
        setFinalContentType(validTypesFromUrl);
      }

      if (validTopicsFromUrl.length > 0) {
        setTopics(validTopicsFromUrl);
        setFinalTopics(validTopicsFromUrl);
      }

      // Apply filters to the query
      if (validTypesFromUrl.length > 0 || validTopicsFromUrl.length > 0) {
        const updatedFilters: (FilterAnyOf | FilterNot)[] = [...baseFilter()];

        if (validTypesFromUrl.length > 0) {
          updatedFilters.push(new FilterAnyOf('type', validTypesFromUrl));
        } else if (typesItems.length > 0) {
          updatedFilters.push(new FilterAnyOf('type', typesItems));
        }

        if (validTopicsFromUrl.length > 0) {
          updatedFilters.push(new FilterAnyOf('topics', validTopicsFromUrl));
        } else if (topicsItems.length > 0) {
          updatedFilters.push(new FilterAnyOf('topics', topicsItems));
        }

        query
          .getRequest()
          .setSearchFacetAll(false)
          .setSearchOffset(0)
          .setSources(searchSourceIds)
          .setSearchFilter(new FilterAnd(updatedFilters));

        filterIncludeResource(validTypesFromUrl, validTopicsFromUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownOpen]);

  React.useEffect(() => {
    if (firstTimeCount !== 0 && !isInitialLoading && resources.length > 0) {
      if (offset > 0) {
        setActualData({
          fetchCount: resourcesData.fetchCount + 1,
          ResourceList: [...resourcesData.ResourceList, ...resources],
        });
        setResourcesData({
          fetchCount: 1,
          ResourceList: [...resourcesData.ResourceList, ...resources],
        });
      } else {
        setResourcesData({
          fetchCount: 1,
          ResourceList: [...resources],
        });
      }
    }

    setFinalResult(total_item);
    // We only need to care about articles and offset as a dependency
    // because load more pagination requires new results to be loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps

    if (firstTimeCount !== 0 && directChange) {
      setActualData({
        fetchCount: 1,
        ResourceList: [...resources],
      });

      setOldResult(total_item);
      if (oldResult !== 0) {
        setDirectChange(false);
      }

      filterIncludeResource(
        contentType.length > 0 ? contentType : typesItems,
        topics.length > 0 ? contentType : topicsItems
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, resources, pathName]);

  React.useEffect(() => {
    if (firstTimeCount == 0 && !isInitialLoading && resources.length > 0) {
      setActualData({
        fetchCount: 1,
        ResourceList: [...resources],
      });
      setResourcesData({
        fetchCount: 1,
        ResourceList: [...resources],
      });

      filterIncludeResource(typesItems, topicsItems);
      setFinalResult(total_item + IncludedResources().length);
      setOldResult(total_item + IncludedResources().length);
      setFirstTimeCount(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources, pathName]);

  React.useEffect(() => {
    if (currentScreenWidth < 768) {
      setIsListView(true);
    }
  }, [currentScreenWidth]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    const checkIfScrollable = () => {
      if (scrollableContentRef.current) {
        const element = scrollableContentRef.current;
        const isContentScrollable = element.scrollHeight > element.clientHeight;
        setIsScrollable(isContentScrollable);
      }
    };

    checkIfScrollable();
    window.addEventListener('resize', checkIfScrollable);

    if (dropdownOpen) {
      checkIfScrollable();
    }

    return () => {
      window.removeEventListener('resize', checkIfScrollable);
    };
  }, [dropdownOpen, contentType, topics]);

  return (
    <section
      className={classNames(
        'container',
        params?.BackgroundColor?.Value?.value,
        params?.Spacing?.Value?.value || 'spacing-md',
        dropdownOpen && 'filterMenuOpen'
      )}
      data-id="resource-library"
    >
      <div className="mx-auto flex flex-col gap-8">
        <Text
          tag={params?.HeadingType?.Tag?.value || 'h2'}
          field={fields?.Title}
          className={classNames(headingClass, params?.HeadingType?.Styles?.value)}
        />
        <RichTextA11yWrapper
          field={fields?.CopyText}
          className={classNames(
            'p2',
            params?.Alignment?.Value?.value === 'text-center'
              ? 'text-center [&_li]:mx-auto [&_li]:w-fit'
              : 'text-left'
          )}
        />
        <div
          className={classNames(
            fields?.DisableFilterPanel?.value && currentScreenWidth < 768 && 'hidden',
            'grid md:grid-cols-3 gap-6 justify-between'
          )}
        >
          {!fields?.DisableFilterPanel?.value && (
            <div
              className="relative col-span-2"
              ref={(el) => {
                dropdownRefs.current = el;
              }}
            >
              <div className="hidden md:block border-b-2  border-light-stroke-primary-high pb-1">
                <Text
                  field={fields?.FilterLabel}
                  tag="span"
                  className="block w-full c1 text-light-fill-primary-variant mb-1"
                />
                <button
                  className="w-full flex justify-between items-center gap-10 text-light-fill-primary-variant"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  title={fields?.FilterPlaceholder?.value}
                  type="button"
                >
                  <Text field={fields?.FilterPlaceholder} tag="span" className="p2" />
                  <SVG svg="icon-carat-down" className="[&_svg]:w-5 [&_svg]:h-5" />
                </button>
              </div>
              <div className="block md:hidden">
                <button
                  className="text-sm font-medium rounded text-light-fill-secondary px-6 py-[10px] border-2 border-light-stroke-primary-variant w-full flex gap-3 justify-center items-center"
                  onClick={() => setDropdownOpen(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setDropdownOpen(true)}
                  title={fields?.FilterLabel?.value}
                  type="button"
                >
                  <SVG svg="icon-sort-filter" className="[&_svg]:w-6 [&_svg]:h-6" />
                  <Text field={fields?.FilterLabel} />
                </button>
              </div>

              <div
                className={classNames(
                  !dropdownOpen && 'hidden',
                  'fixed md:absolute left-0 top-0 md:top-full flex flex-col items-center md:shadow-small bg-light-background-neutral z-[9999] md:z-[11] w-full h-full md:h-fit'
                )}
              >
                <button
                  className="inline-flex md:hidden absolute top-4 right-4 w-8 h-8 justify-center items-center text-light-fill-secondary"
                  onClick={closeDropDown}
                  onKeyDown={(e) => e.key === 'Enter' && closeDropDown()}
                  title="close"
                >
                  <SVG svg="icon-close" className="[&_svg]:w-5 [&_svg]:h-5 inline-block" />
                </button>
                <div
                  ref={scrollableContentRef}
                  className={`w-full p-6 md:p-8 grow overflow-y-auto max-h-screen md:max-h-[30rem] ${
                    isScrollable ? 'bok-scrollbar' : ''
                  }`}
                >
                  {fields?.ContentType && fields?.ContentType.length > 1 && (
                    <>
                      <div className="mb-8">
                        <Text tag="p" field={fields?.FilterTypes} className="p2-medium mb-4" />
                        <ul className="flex flex-wrap gap-4">
                          {fields?.ContentType?.map((item) => {
                            if (!item) return null;

                            return (
                              <li
                                tabIndex={0}
                                className={classNames(
                                  'inline-flex items-center justify-center gap-2 p-2 rounded cursor-pointer p3',
                                  'border border-light-stroke-primary-variant hover:border-light-stroke-primary-variant-high hover:underline',
                                  'focus:outline-none focus:border-light-stroke-primary-variant-highest focus:font-medium',
                                  contentType &&
                                    contentType.some((type) =>
                                      type.toLowerCase() == 'videoresource'
                                        ? 'video' ===
                                          (item?.fields?.Name as Field<string>)?.value.toLowerCase()
                                        : type.toLowerCase() == 'calculatorresource'
                                        ? 'calculator' ===
                                          (item?.fields?.Name as Field<string>)?.value.toLowerCase()
                                        : type === (item?.fields?.Name as Field<string>)?.value
                                    ) &&
                                    'text-light-fill-inverse bg-light-background-primary'
                                )}
                                key={item.id}
                                onClick={() =>
                                  activeTypes({
                                    type: (item?.fields?.Name as Field<string>)?.value,
                                  })
                                }
                                onKeyDown={(e) =>
                                  e.key === 'Enter' &&
                                  activeTypes({
                                    type: (item?.fields?.Name as Field<string>)?.value,
                                  })
                                }
                              >
                                <SVG
                                  className="[&_svg]:w-4 [&_svg]:h-4 [&_svg]:md:w-6 [&_svg]:md:h-6"
                                  svg={(item?.fields?.Icon as IconProps)?.fields?.Value?.value}
                                />
                                <Text field={item?.fields?.Name as Field<string>} />
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      <hr
                        className={`m-0 ${fields?.Topics && fields?.Topics.length > 0 && 'mb-8'}`}
                      />
                    </>
                  )}
                  {fields?.Topics && fields?.Topics.length > 0 && (
                    <div className="relative">
                      <Text tag="p" field={fields?.FilterTopics} className="p2-medium mb-4" />
                      <ul className="flex flex-col gap-6">
                        {fields?.Topics?.map((item, index) => {
                          if (!item) return null;
                          return (
                            <li key={item.id} className="flex">
                              <div
                                className={`inline-flex items-center`}
                                onClick={() =>
                                  activeTopics({
                                    topic: (item?.fields?.Name as Field<string>)?.value,
                                  })
                                }
                                onKeyDown={(e) =>
                                  e.key === 'Enter' &&
                                  activeTopics({
                                    topic: (item?.fields?.Name as Field<string>)?.value,
                                  })
                                }
                              >
                                <input
                                  type="checkbox"
                                  title={(item?.fields?.Name as Field<string>).value}
                                  id={'label-' + index}
                                  checked={topics.some(
                                    (topic) =>
                                      topic === (item?.fields?.Name as Field<string>)?.value
                                  )}
                                  className={classNames(
                                    'p-0 mb-0 cursor-pointer absolute z-[1] opacity-0 peer',
                                    'h-4 w-4 md:h-6 md:w-6'
                                  )}
                                />
                                <Text
                                  tag="label"
                                  htmlFor={'#label-' + index}
                                  field={item?.fields?.Name as Field<string>}
                                  class={checkBokStyle()}
                                />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="bg-light-background-neutral py-6 px-8 w-full shadow-small flex flex-wrap gap-x-5 gap-y-3 justify-between items-center">
                  <button
                    className="text-light-fill-secondary text-lg leading-5 font-medium"
                    onClick={clearFilter}
                    onKeyDown={(e) => e.key === 'Enter' && clearFilter}
                    title={(fields?.FilterClearAll as Field<string>).value}
                  >
                    <Text field={fields?.FilterClearAll as Field<string>} />
                  </button>
                  <button
                    className="text-lg font-medium leading-5 rounded text-light-fill-secondary px-6 py-3 md:p-4 border-2 border-light-stroke-primary-variant min-w-[207px] md:min-w-[304px] disabled:opacity-10"
                    onClick={() => viewResult()}
                    onKeyDown={(e) => e.key === 'Enter' && viewResult()}
                    disabled={isFetching}
                  >
                    <Text field={fields?.FilterViewResults as Field<string>} />
                    <span className="ms-1">({finalResult})</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={classNames(
              fields?.DisableFilterPanel?.value ? 'col-span-3' : 'col-span-1',
              'md:flex justify-end items-center hidden'
            )}
          >
            <div className="inline-flex items-center relative h-fit ms-auto">
              <button
                className={`transition-all inline-flex items-center border-2 border-light-stroke-primary-variant rounded-l py-2 px-[22px] hover:bg-light-background-neutral-highest focus:bg-light-background-neutral-high border-r-0 outline-none ${
                  isListView &&
                  '!bg-light-background-primary focus:!bg-light-background-primary-high hover:!bg-light-background-primary-highest !border-light-stroke-primary-variant focus:!border-light-stroke-primary-variant-high hover:!border-light-stroke-primary-variant-highest'
                }`}
                onClick={() => setIsListView(true)}
                type="button"
                title="List"
              >
                <SVG
                  svg="icon-sort-list-view"
                  className={`[&_svg]:w-5 [&_svg]:h-5 mr-2 ${
                    !isListView ? 'text-light-fill-secondary' : 'text-light-fill-inverse'
                  }`}
                />
                <Text
                  tag="span"
                  field={{ value: 'List' }}
                  className={classNames(
                    isListView ? 'text-light-fill-inverse' : 'text-light-fill-secondary',
                    'c1-medium outline-none'
                  )}
                />
              </button>
              <button
                className={`transition-all inline-flex items-center border-2 border-light-stroke-primary-variant rounded-r py-2 px-[22px] hover:bg-light-background-neutral-highest border-l-0 focus:bg-light-background-neutral-high outline-none ${
                  !isListView &&
                  '!bg-light-background-primary focus:!bg-light-background-primary-high hover:!bg-light-background-primary-highest !border-light-stroke-primary-variant focus:!border-light-stroke-primary-variant-high hover:!border-light-stroke-primary-variant-highest'
                }`}
                onClick={() => setIsListView(false)}
                type="button"
                title="Grid"
              >
                <SVG
                  svg="icon-grid"
                  className={`[&_svg]:w-5 [&_svg]:h-5 mr-2 ${
                    isListView ? 'text-light-fill-secondary' : 'text-light-fill-inverse'
                  }`}
                />
                <Text
                  tag="span"
                  field={{ value: 'Grid' }}
                  className={classNames(
                    !isListView ? 'text-light-fill-inverse' : 'text-light-fill-secondary',
                    'c1-medium outline-none'
                  )}
                />
              </button>
            </div>
          </div>
        </div>

        {(finalContentType.length > 0 || finalTopics.length > 0) && (
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            {finalContentType.length > 0 &&
              finalContentType.map((item, index) => (
                <button
                  className="inline-flex gap-2 items-center p-2 border-2 border-light-stroke-primary-variant hover:underline hover:border-light-stroke-primary-variant-high focus:border-light-stroke-primary-variant-highest rounded p3-medium outline-none"
                  onClick={() =>
                    activeTypes({
                      type: item,
                      isDropdownValue: true,
                    })
                  }
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    activeTypes({
                      type: item,
                      isDropdownValue: true,
                    })
                  }
                  key={index}
                  type="button"
                  title={getResourceTypeDisplayName(item)}
                >
                  <Text
                    tag="span"
                    field={{
                      value: getResourceTypeDisplayName(item),
                    }}
                  />
                  <SVG svg="icon-close" className="[&_svg]:w-3 [&_svg]:h-3" />
                </button>
              ))}
            {finalTopics.length > 0 &&
              finalTopics.map((item, index) => (
                <button
                  className="inline-flex gap-2 items-center p-2 border-2 border-light-stroke-primary-variant hover:border-light-stroke-primary-variant-high hover:underline focus:border-light-stroke-primary-variant-highest rounded p3-medium outline-none"
                  onClick={() =>
                    activeTopics({
                      topic: item,
                      isDropdownValue: true,
                    })
                  }
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    activeTopics({
                      topic: item,
                      isDropdownValue: true,
                    })
                  }
                  key={index}
                  type="button"
                  title={item}
                >
                  <Text tag="span" field={{ value: item }} />
                  <SVG svg="icon-close" className="[&_svg]:w-3 [&_svg]:h-3" />
                </button>
              ))}
            <button
              className="p2 text-light-fill-secondary"
              onClick={clearFilter}
              onKeyDown={(e) => e.key === 'Enter' && clearFilter}
              title={(fields?.FilterClearAll as Field<string>).value}
              type="button"
            >
              <Text field={fields?.FilterClearAll as Field<string>} />
            </button>
          </div>
        )}

        {oldResult === 0 && isFetched ? (
          <div className="max-w-[830px] mx-auto text-center">
            <ImageWrapper
              field={fields?.NoContentImage}
              className="w-[120px] h-[120px] md:w-[140px] md:h-[140px] lg:w-[180px] lg:h-[180px] object-fit object-center aspect-square mx-auto mb-6"
              width="180"
              height="180"
            />
            <RichTextA11yWrapper tag="h3" field={fields?.NoContentTitle} className="mb-6" />
            <RichTextA11yWrapper tag="p" field={fields?.NoContentText} className="p2" />
          </div>
        ) : (
          <>
            {/* Result list */}
            {isListView ? (
              <ul className="grid grid-cols-1 gap-6">
                {includedResource &&
                  includedResource.length > 0 &&
                  includedResource.map((resource) => (
                    <li className="col-span-1" key={resource.id} data-resource={resource.id}>
                      <LinkWrapper
                        field={{ value: { href: resource.url } }}
                        tabIndex={0}
                        className="w-full bg-light-background-neutral-variant block border border-light-stroke-primary rounded-t-lg overflow-hidden border-b-light-stroke-primary-variant-primary-variant !no-underline"
                      >
                        <Text
                          field={fields?.StatementLabel}
                          tag="span"
                          class="inline-block bottom-0 left-0 py-1 px-3 bg-light-background-secondary-brand text-light-fill-inverse text-sm"
                        />
                        <div className="flex flex-wrap md:flex-nowrap gap-x-9 gap-y-2 px-4 py-2 md:p-4">
                          <RichTextA11yWrapper
                            className="w-full md:w-[calc(33%-32px)]"
                            tag="h4"
                            field={{ value: resource.name }}
                          />
                          <RichTextA11yWrapper
                            className="text-light-fill-primary-variant p3 w-full md:w-[67%]"
                            field={{
                              value: resource.description,
                            }}
                          />
                        </div>
                        <div className="flex gap-8 px-4 pb-2 md:p-4 md:pt-0 justify-between">
                          <p className="h-full flex items-center justify-center c1">
                            {resource.publish_date &&
                              formatTimestamp(resource.publish_date, 'Month DD, YYYY')}
                            <span className="inline-block mx-2 w-px h-3 bg-light-other-ink" />
                            {resource.article_time}
                          </p>

                          <span className="text-light-fill-secondary inline-flex items-center gap-2 c1-medium">
                            {resource.type && resource.type.toLowerCase() == 'videoresource' && (
                              <Text tag="span" field={{ value: 'Watch' }} />
                            )}
                            {resource.type &&
                              resource.type.toLowerCase() == 'calculatorresource' && (
                                <Text field={{ value: 'Calculator' }} />
                              )}
                            {resource.type && resource.type.toLowerCase() == 'article' && (
                              <Text tag="span" field={{ value: 'Read' }} />
                            )}

                            {fields?.ContentType &&
                              fields?.ContentType.map((item: ElementProps, index) => {
                                if (
                                  (item.fields?.Name as Field<string>).value.toLowerCase() ==
                                  getResourceFilterType(resource.type || '')
                                ) {
                                  return (
                                    <SVG
                                      className="[&_svg]:w-5 [&_svg]:h-5 [&_svg]:md:w-6 [&_svg]:md:h-6"
                                      svg={(item?.fields?.Icon as IconProps)?.fields?.Value?.value}
                                      key={index}
                                    />
                                  );
                                }
                                return null;
                              })}
                          </span>
                        </div>
                      </LinkWrapper>
                    </li>
                  ))}
                {actualData.ResourceList &&
                  actualData.ResourceList.map((resource) => (
                    <li className="col-span-1" key={resource.id} data-resource={resource.id}>
                      <LinkWrapper
                        field={{ value: { href: resource.url } }}
                        tabIndex={0}
                        className="w-full bg-light-background-neutral-variant block border border-light-stroke-primary rounded-t-lg overflow-hidden border-b-light-stroke-primary-variant !no-underline"
                      >
                        <Text
                          field={fields?.StatementLabel}
                          tag="span"
                          class="inline-block bottom-0 left-0 py-1 px-3 bg-light-background-secondary-brand text-light-fill-inverse text-sm"
                        />
                        <div className="flex flex-wrap md:flex-nowrap gap-x-9 gap-y-2 px-4 py-2 md:p-4">
                          <RichTextA11yWrapper
                            className="w-full md:w-[calc(33%-32px)]"
                            tag="h4"
                            field={{ value: resource.name }}
                          />
                          <RichTextA11yWrapper
                            className="text-light-fill-primary-variant p3 w-full md:w-[67%]"
                            field={{
                              value: resource.description,
                            }}
                          />
                        </div>
                        <div className="flex gap-8 px-4 pb-2 md:p-4 md:pt-0 justify-between">
                          <p className="h-full flex items-center justify-center c1">
                            {resource.publish_date &&
                              formatTimestamp(resource.publish_date, 'Month DD, YYYY')}
                            <span className="inline-block mx-2 w-px h-3 bg-light-other-ink" />
                            {resource.article_time}
                          </p>

                          <span className="text-light-fill-secondary inline-flex items-center gap-2 c1-medium">
                            {resource.type.toLowerCase() == 'videoresource' && (
                              <Text tag="span" field={{ value: 'Watch' }} />
                            )}
                            {resource.type.toLowerCase() == 'calculatorresource' && (
                              <Text field={{ value: 'Calculator' }} />
                            )}
                            {resource.type.toLowerCase() == 'article' && (
                              <Text tag="span" field={{ value: 'Read' }} />
                            )}

                            {fields?.ContentType &&
                              fields?.ContentType.map((item) => {
                                if (
                                  (item.fields?.Name as Field<string>).value.toLowerCase() ==
                                  getResourceFilterType(resource.type)
                                ) {
                                  return (
                                    <SVG
                                      className="[&_svg]:w-5 [&_svg]:h-5 [&_svg]:md:w-6 [&_svg]:md:h-6"
                                      svg={(item?.fields?.Icon as IconProps)?.fields?.Value?.value}
                                      key={item.id}
                                    />
                                  );
                                }
                                return null;
                              })}
                          </span>
                        </div>
                      </LinkWrapper>
                    </li>
                  ))}
              </ul>
            ) : (
              <ul className="grid grid-cols-2 lg:grid-cols-3 grid-flow-dense gap-6">
                {includedResource &&
                  includedResource.length > 0 &&
                  includedResource.map((resource) => (
                    <li
                      className={classNames(
                        resource.type &&
                          resource.type.toLowerCase() == 'videoresource' &&
                          'col-span-1 row-span-2 lg:col-span-2 lg:row-span-1',
                        resource.type &&
                          resource.type.toLowerCase() == 'calculatorresource' &&
                          'col-span-1 row-span-1',
                        resource.type &&
                          resource.type.toLowerCase() == 'article' &&
                          'col-span-1 row-span-2'
                      )}
                      key={resource.id}
                    >
                      <LinkWrapper
                        tabIndex={0}
                        field={{ value: { href: resource.url } }}
                        className={classNames(
                          resource.type && resource.type.toLowerCase() == 'videoresource'
                            ? 'flex-col lg:flex-row'
                            : 'flex-col',
                          'flex items-start h-full w-full bg-light-background-neutral-variant border border-default-3 rounded-t-lg border-b-light-stroke-primary-variant overflow-hidden focus:border-light-stroke-primary-variant-highest focus-visible:outline-none hover:border-light-stroke-primary-low hover:shadow-[0px_2px_40px_0px_rgba(57,100,191,0.15)] hover:no-underline text-inherit hover:text-inherit focus:no-underline hover:border-b-light-stroke-primary-variant'
                        )}
                      >
                        {resource.type && resource.type.toLowerCase() != 'calculatorresource' && (
                          <div
                            className={classNames(
                              resource.type && resource.type.toLowerCase() == 'videoresource'
                                ? 'w-full lg:w-[60%] h-full min-h-[200px] lg:min-h-[260px] after:absolute after:bg-[#000] after:opacity-50 after:w-full after:h-full after:top-0 after:left-0'
                                : 'w-full min-h-[200px]',
                              'relative'
                            )}
                          >
                            <ImageWrapper
                              field={{
                                value: {
                                  src: resource.image_url,
                                },
                              }}
                              className="w-full max-h-[200px] h-full object-cover object-center aspect-[356/200]"
                              width="325"
                              height="192"
                            />

                            <Text
                              field={fields?.StatementLabel}
                              tag="span"
                              class="absolute inline-block bottom-0 left-0 py-1 px-3 bg-light-background-secondary-brand text-light-fill-inverse text-sm"
                            />
                            {resource.type && resource.type.toLowerCase() == 'videoresource' && (
                              <SVG
                                svg="icon-play"
                                hidden={false}
                                className="absolute inline-block w-10 h-10 lg:w-13 lg:h-13 text-light-fill-inverse top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                              />
                            )}
                          </div>
                        )}
                        {/* Category colored stripe */}

                        <div
                          className={classNames(
                            resource.type && resource.type.toLowerCase() == 'videoresource'
                              ? 'w-full lg:w-[40%]'
                              : 'w-full',
                            'flex flex-col justify-between p-4 h-full'
                          )}
                        >
                          <div>
                            <RichTextA11yWrapper
                              className="mb-3 lg:mb-4.5"
                              tag="h4"
                              field={{ value: resource.name }}
                            />
                            <RichTextA11yWrapper
                              className="mb-4 lg:mb-6 text-light-fill-primary-variant p3"
                              field={{
                                value: resource.description,
                              }}
                            />
                          </div>
                          <div className="flex flex-wrap items-center justify-between c1 gap-x-5 gap-y-2">
                            <p className="flex items-center justify-center text-light-fill-primary">
                              {resource.publish_date &&
                                formatTimestamp(resource.publish_date, 'Month DD, YYYY')}
                              <span className="inline-block mx-2 w-px h-4 bg-light-other-ink" />
                              {resource.article_time}
                            </p>
                            <span className="text-light-fill-secondary inline-flex items-center gap-2 c1-medium">
                              {resource.type && resource.type.toLowerCase() == 'videoresource' && (
                                <Text tag="span" field={{ value: 'Watch' }} />
                              )}
                              {resource.type &&
                                resource.type.toLowerCase() == 'calculatorresource' && (
                                  <Text field={{ value: 'Calculator' }} />
                                )}
                              {resource.type && resource.type.toLowerCase() == 'article' && (
                                <Text tag="span" field={{ value: 'Read' }} />
                              )}

                              {fields?.ContentType &&
                                fields?.ContentType.map((item) => {
                                  if (
                                    (item.fields?.Name as Field<string>).value.toLowerCase() ==
                                    getResourceFilterType(resource.type || '')
                                  ) {
                                    return (
                                      <SVG
                                        className="[&_svg]:w-6 [&_svg]:h-6"
                                        svg={
                                          (item?.fields?.Icon as IconProps)?.fields?.Value?.value
                                        }
                                        key={item.id}
                                      />
                                    );
                                  }
                                  return null;
                                })}
                            </span>
                          </div>
                        </div>
                      </LinkWrapper>
                    </li>
                  ))}
                {actualData.ResourceList &&
                  actualData.ResourceList.map((resource, index) => (
                    <li
                      className={classNames(
                        resource.type.toLowerCase() == 'videoresource' &&
                          'col-span-1 row-span-2 lg:col-span-2 lg:row-span-1',
                        resource.type.toLowerCase() == 'calculatorresource' &&
                          'col-span-1 row-span-1',
                        resource.type.toLowerCase() == 'article' && 'col-span-1 row-span-2'
                      )}
                      key={index}
                    >
                      <LinkWrapper
                        tabIndex={0}
                        field={{ value: { href: resource.url } }}
                        className={classNames(
                          resource.type.toLowerCase() == 'videoresource'
                            ? 'flex-col lg:flex-row'
                            : 'flex-col',
                          'flex items-start h-full w-full bg-light-background-neutral-variant border border-default-3 rounded-t-lg border-b-light-stroke-primary-variant overflow-hidden focus-visible:outline-none hover:border-light-stroke-primary-low hover:shadow-[0px_2px_40px_0px_rgba(57,100,191,0.15)] hover:no-underline text-inherit hover:text-inherit focus:no-underline hover:border-b-light-stroke-primary-variant focus:border-light-stroke-primary-variant-highest'
                        )}
                      >
                        {resource.type.toLowerCase() != 'calculatorresource' && (
                          <div
                            className={classNames(
                              resource.type.toLowerCase() == 'videoresource'
                                ? 'w-full lg:w-[60%] h-full min-h-[200px] lg:min-h-[260px] after:absolute after:bg-[#000] after:opacity-50 after:w-full after:h-full after:top-0 after:left-0'
                                : 'w-full min-h-[200px]',
                              'relative'
                            )}
                          >
                            {resource.type.toLowerCase() == 'videoresource' ? (
                              <ImageWrapper
                                field={{
                                  value: {
                                    src: resource.image_url,
                                  },
                                }}
                                className="w-full h-full object-cover object-center"
                                layout="fill"
                              />
                            ) : (
                              <ImageWrapper
                                field={{
                                  value: {
                                    src: resource.image_url,
                                  },
                                }}
                                className="w-full max-h-[200px] h-full object-cover object-center aspect-[356/200]"
                                width="325"
                                height="192"
                              />
                            )}
                            <Text
                              field={fields?.StatementLabel}
                              tag="span"
                              class="absolute inline-block bottom-0 left-0 py-1 px-3 bg-light-background-secondary-brand text-light-fill-inverse text-sm"
                            />
                            {resource.type.toLowerCase() == 'videoresource' && (
                              <SVG
                                svg="icon-play"
                                hidden={false}
                                className="absolute inline-block w-10 h-10 lg:w-13 lg:h-13 text-light-fill-inverse top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                              />
                            )}
                          </div>
                        )}
                        {/* Category colored stripe */}

                        <div
                          className={classNames(
                            resource.type.toLowerCase() == 'videoresource'
                              ? 'w-full lg:w-[40%]'
                              : 'w-full',
                            'flex flex-col justify-between p-4 h-full'
                          )}
                        >
                          <div>
                            <RichTextA11yWrapper
                              className="mb-3 lg:mb-4.5"
                              tag="h4"
                              field={{ value: resource.name }}
                            />
                            <RichTextA11yWrapper
                              className="mb-4 lg:mb-6 text-light-fill-primary-variant p3"
                              field={{
                                value: resource.description,
                              }}
                            />
                          </div>
                          <div className="flex flex-wrap items-center justify-between c1 gap-x-5 gap-y-2">
                            <p className="flex items-center justify-center text-light-fill-primary">
                              {resource.publish_date &&
                                formatTimestamp(resource.publish_date, 'Month DD, YYYY')}
                              <span className="inline-block mx-2 w-px h-4 bg-light-other-ink" />
                              {resource.article_time}
                            </p>
                            <span className="text-light-fill-secondary inline-flex items-center gap-2 c1-medium">
                              {resource.type.toLowerCase() == 'videoresource' && (
                                <Text tag="span" field={{ value: 'Watch' }} />
                              )}
                              {resource.type.toLowerCase() == 'calculatorresource' && (
                                <Text field={{ value: 'Calculator' }} />
                              )}
                              {resource.type.toLowerCase() == 'article' && (
                                <Text tag="span" field={{ value: 'Read' }} />
                              )}

                              {fields?.ContentType &&
                                fields?.ContentType.map((item) => {
                                  if (
                                    (item.fields?.Name as Field<string>).value.toLowerCase() ==
                                    getResourceFilterType(resource.type)
                                  ) {
                                    return (
                                      <SVG
                                        className="[&_svg]:w-6 [&_svg]:h-6"
                                        svg={
                                          (item?.fields?.Icon as IconProps)?.fields?.Value?.value
                                        }
                                        key={item.id}
                                      />
                                    );
                                  }
                                  return null;
                                })}
                            </span>
                          </div>
                        </div>
                      </LinkWrapper>
                    </li>
                  ))}
              </ul>
            )}

            {loadMoreOptions && (
              <div className="text-center">
                <button
                  disabled={limit + offset === finalResult}
                  className="inline-flex items-center gap-x-3"
                  onClick={loadMoreResults}
                  onKeyDown={(e) => e.key === 'Enter' && loadMoreResults()}
                  title={fields?.ViewMoreContent?.value}
                >
                  <Text
                    tag="span"
                    field={fields?.ViewMoreContent}
                    className="p1-medium text-light-fill-secondary disabled:text-light-fill-primary-variant"
                  />
                  <SVG
                    svg="icon-chevron-down"
                    className="text-light-fill-secondary disabled:text-light-fill-primary-variant [&_svg]:w-5 [&_svg]:h-5 [&_svg]:md:w-6 [&_svg]:md:h-6"
                  />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export const ResourceWidget = widget(
  ResourceLibrarySearch,
  WidgetDataType.SEARCH_RESULTS,
  'content'
);
